import { Injectable } from '@angular/core';
import { BehaviorSubject, concat } from 'rxjs';
import { User } from './../models/user.model';
import { CindiMessage } from './../models/cindi-message.model';
import { UserAnswer } from './../models/user-answer.model';
import { UserQuestionnaire } from './../models/user-questionnaire.model';
import { Alternative } from './../models/alternative.model';
import { CindiService } from './cindi.service';
import axiosClient from './axios-client';

@Injectable({
  providedIn: 'root',
})
export class AnswerService {
  public user: User = new User();
  public hasIntensePain: boolean = true;
  private userQuestionnaire: UserQuestionnaire = new UserQuestionnaire();
  private allUserAnswers: UserAnswer[] = [];

  constructor(private cindiService: CindiService) {}

  private progressBarCountSubject = new BehaviorSubject<number>(5);
  progressBarCount$ = this.progressBarCountSubject.asObservable();

  get currentProgressBarCount(): number {
    return this.progressBarCountSubject.value;
  }

  private previousQuestionIdSubject = new BehaviorSubject<number | null>(null);
  previousQuestionId$ = this.previousQuestionIdSubject.asObservable();

  get previousQuestionId(): number | null {
    return this.previousQuestionIdSubject.value;
  }

  private continueQuestionsFlowSubject = new BehaviorSubject<boolean>(false);
  continueQuestionsFlow$ = this.continueQuestionsFlowSubject.asObservable();

  get continueQuestionsFlow(): boolean {
    return this.continueQuestionsFlowSubject.value;
  }

  private hasLowerBackPainSubject = new BehaviorSubject<boolean>(false);
  hasLowerBackPain$ = this.hasLowerBackPainSubject.asObservable();

  get hasLowerBackPain(): boolean {
    return this.hasLowerBackPainSubject.value;
  }

  private hasAcutePainSubject = new BehaviorSubject<boolean>(false);
  hasAcutePain$ = this.hasAcutePainSubject.asObservable();

  get hasAcutePain(): boolean {
    return this.hasAcutePainSubject.value;
  }

  private hasSecondaryDiseaseSubject = new BehaviorSubject<boolean>(false);
  hasSecondaryDisease$ = this.hasSecondaryDiseaseSubject.asObservable();

  get hasSecondaryDisease(): boolean {
    return this.hasSecondaryDiseaseSubject.value;
  }

  private hasOtherSymptomsSubject = new BehaviorSubject<boolean>(false);
  hasOtherSymptoms$ = this.hasOtherSymptomsSubject.value;

  get hasOtherSymptoms(): boolean {
    return this.hasOtherSymptomsSubject.value;
  }

  private hasRedFlagsSubject = new BehaviorSubject<boolean>(false);
  hasRedFlags$ = this.hasRedFlagsSubject.value;

  get hasRedFlags(): boolean {
    return this.hasRedFlagsSubject.value;
  }

  setHasOtherSymptoms(value: boolean): void {
    this.hasOtherSymptomsSubject.next(value);
  }

  setContinueQuestionsFlow(value: boolean): void {
    this.continueQuestionsFlowSubject.next(value);
  }

  setHasRedFlags(value: boolean): void {
    this.hasRedFlagsSubject.next(value);
  }

  setProgressBarProgressPositive(value: number) {
    let currentBarValue = this.progressBarCountSubject.value;
    this.progressBarCountSubject.next(currentBarValue + value);
  }

  setPreviousQuestionId(id: number) {
    this.previousQuestionIdSubject.next(id);
  }

  setHasLowerBackPain(value: boolean) {
    this.hasLowerBackPainSubject.next(value);
  }

  setHasAcutePain(value: boolean) {
    this.hasAcutePainSubject.next(value);
  }

  setHasSecondaryDisease(value: boolean) {
    this.hasSecondaryDiseaseSubject.next(value);
  }

  private personalDataSubject = new BehaviorSubject<any>({
    pais: null,
    cidadeResidencia: null,
    estadoCivil: null,
    grauEscolaridade: null,
    classificacaoEtnicoRacial: null,
    cargo: null,
    area: null,
    turno: null,
  });

  personalData$ = this.personalDataSubject.asObservable();

  get personalData() {
    return this.personalDataSubject.value;
  }

  setPersonalData(data: any) {
    this.personalDataSubject.next({
      ...this.personalData,
      ...data,
    });
  }

  setPersonalField(field: string, value: any) {
    this.personalDataSubject.next({
      ...this.personalData,
      [field]: value,
    });
  }

  private getDynamicProgressIncrement(): number {
    const fields = [
      'pais',
      'cidadeResidencia',
      'estadoCivil',
      'grauEscolaridade',
      'classificacaoEtnicoRacial',
      'cargo',
      'area',
      'turno',
    ];

    const emptyFieldsCount = fields.filter(
      (field) =>
        this.personalData[field] === null || this.personalData[field] === '',
    ).length;

    if (emptyFieldsCount >= 6) return 2;
    if (emptyFieldsCount >= 3) return 3;
    return 5;
  }

  async setAnswer(question: CindiMessage, index: number) {
    const isPersonalData = question.id >= 9 && question.id <= 16;
    const increment = isPersonalData ? this.getDynamicProgressIncrement() : 3;

    const selectedAlternative = question.alternatives[index];
    if (selectedAlternative.id == 2 || question.id == 44) {
      this.setProgressBarProgressPositive(86);
    }
    if (
      selectedAlternative.id == 7 ||
      selectedAlternative.id == 4 ||
      selectedAlternative.id == 5
    ) {
      this.setProgressBarProgressPositive(40);
    } else {
      this.setProgressBarProgressPositive(increment);
    }

    await this.disablePreviousOptionsAndMessages();

    const userAnswer: UserAnswer = new UserAnswer(
      this.userQuestionnaire.pessoaQuestionarioId,
      question.id,
      question.tagMensagem,
      [selectedAlternative],
      null,
    );

    this.allUserAnswers.push(userAnswer);

    if (question.id >= 19 && question.id <= 27) {
      this.cindiService.addPontuation(selectedAlternative.pontuacao!);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.id == 18) {
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.id == 4) {
      this.setHasLowerBackPainFlow(selectedAlternative.id);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.id == 5) {
      this.setHasAcutePainFlow(selectedAlternative.id);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.id == 6) {
      this.setHasSecondaryDiseaseFlow(selectedAlternative.id);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.tagMensagem === 'Outros sintomas') {
      this.setHasOtherSymptomsFlow(selectedAlternative.id);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.id == 8) {
      this.setHasRedFlagsFlow(selectedAlternative.id);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.id >= 9 && question.id <= 16) {
      const fieldsMap: any = {
        9: 'pais',
        10: 'cidadeResidencia',
        11: 'estadoCivil',
        12: 'grauEscolaridade',
        13: 'classificacaoEtnicoRacial',
        14: 'cargo',
        15: 'area',
        16: 'turno',
      };

      const field = fieldsMap[question.id];

      if (selectedAlternative?.texto) {
        this.setPersonalField(field, selectedAlternative.texto);
      }

      this.setContinueQuestionsFlow(true);
    } else if (question.id >= 28) {
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    }
  }

  async setMultipleAnswers(
    question: CindiMessage,
    alternativesArray: Alternative[],
  ) {
    const isPersonalData = question.id >= 9 && question.id <= 16;
    const increment = isPersonalData ? this.getDynamicProgressIncrement() : 10;
    this.setProgressBarProgressPositive(increment);

    if (alternativesArray.length > 0) {
      const userAnswer: UserAnswer = new UserAnswer(
        this.userQuestionnaire.pessoaQuestionarioId,
        question.id,
        question.tagMensagem,
        alternativesArray,
        null,
      );

      const fieldsMap: any = {
        9: 'pais',
        10: 'cidadeResidencia',
        11: 'estadoCivil',
        12: 'grauEscolaridade',
        13: 'classificacaoEtnicoRacial',
        14: 'cargo',
        15: 'area',
        16: 'turno',
      };

      const field = fieldsMap[question.id];

      if (field) {
        const selectedTexts = alternativesArray
          .map((a) => a.texto)
          .filter((t) => t && t.trim() !== '')
          .join(', ');

        this.setPersonalField(field, selectedTexts);
      }

      this.allUserAnswers.push(userAnswer);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    }
  }

  async setOpenAnswer(question: CindiMessage, inputText: string) {
    const isPersonalData = question.id >= 9 && question.id <= 16;
    const increment = isPersonalData ? this.getDynamicProgressIncrement() : 8;
    this.setProgressBarProgressPositive(increment);
    await this.disablePreviousOptionsAndMessages();

    const userAnswer: UserAnswer = new UserAnswer(
      this.userQuestionnaire.pessoaQuestionarioId,
      question.id,
      question.tagMensagem,
      null,
      inputText,
    );

    if (question.id >= 9 && question.id <= 16) {
      const fieldsMap: any = {
        9: 'pais',
        10: 'cidadeResidencia',
      };
      const field = fieldsMap[question.id];
      this.setPersonalField(field, inputText);
    }

    if (inputText == '') return;
    this.allUserAnswers.push(userAnswer);

    this.previousQuestionIdSubject.next(question.id);
    this.setContinueQuestionsFlow(true);
  }

  async disablePreviousOptionsAndMessages() {
    document.querySelectorAll('.help-text').forEach((p) => p.remove());

    document.querySelectorAll('.rate-scale-img').forEach((e) => {
      e.classList.add('lowlight');
    });

    document.querySelectorAll('.answer-option').forEach((btn) => {
      if (
        btn instanceof HTMLButtonElement &&
        !btn.classList.contains('checkbox-option') &&
        !btn.classList.contains('send-btn')
      ) {
        btn.classList.add('option-unselected-disabled');
      }
    });

    document.querySelectorAll('.answer-option.lowlight').forEach((btn) => {
      if (
        btn instanceof HTMLButtonElement &&
        !btn.classList.contains('checkbox-option') &&
        !btn.classList.contains('send-btn')
      ) {
        btn.classList.remove('lowlight', 'option-unselected-disabled');
        btn.classList.add('option-selected-disabled');
      }
    });

    document.querySelectorAll('.bot-msg').forEach((p) => {
      p.classList.add('msg-disabled');
    });
  }

  setHasOtherSymptomsFlow(alternative: number) {
    if (alternative === 79) this.setHasOtherSymptoms(true);
    else if (alternative === 78) this.setHasOtherSymptoms(false);
  }

  setHasRedFlagsFlow(alternative: number) {
    if (alternative === 7) this.setHasRedFlags(true);
    else if (alternative === 8) this.setHasRedFlags(false);
  }

  setHasLowerBackPainFlow(alternative: number) {
    if (alternative === 1) this.setHasLowerBackPain(true);
    else if (alternative === 2) this.setHasLowerBackPain(false);
  }

  setHasAcutePainFlow(alternative: number) {
    if (alternative === 4) this.setHasAcutePain(true);
    else if (alternative === 3) this.setHasAcutePain(false);
  }

  setHasSecondaryDiseaseFlow(alternative: number) {
    if (alternative === 5) this.setHasSecondaryDisease(true);
    else if (alternative === 6) this.setHasSecondaryDisease(false);
  }

  getNextQuestionId() {
    return this.userQuestionnaire.proximaMensagemId;
  }

  async createAnonymousUserQuestionnaire() {
    return await axiosClient
      .post('api/cindi/user-questionnaires/1', {})
      .then((res: any) => {
        this.userQuestionnaire = res.data.data;
      });
  }

  async saveUserAnswers(questionnaireAnswers: UserAnswer[]) {
    const filteredAnswers = questionnaireAnswers.filter(
      (answer) => answer.questionTag !== 'Decisao do fluxo',
    );
    await axiosClient.post('api/cindi/user-answers', filteredAnswers);
  }

  async saveAnswersByQuestionnaire() {
    await this.saveUserAnswers(this.allUserAnswers);
  }

  async setUserQuestionnaireStatus(status: string) {
    this.userQuestionnaire.status = status;

    await axiosClient.put(
      'api/cindi/user-questionnaires',
      this.userQuestionnaire,
    );
  }

  async loadPersonalDataFromApi(userId: number) {
    try {
      const apiResponse = await axiosClient.get('api/pessoa/' + userId);
      const data = apiResponse.data;

      this.personalDataSubject.next({
        ...this.personalData,
        ...data,
      });

      return data;
    } catch (e) {
      console.error('Erro ao buscar dados pessoais', e);
      return null;
    }
  }

  async createUserQuestionnaire(idPessoa: number) {
    return await axiosClient
      .post(`api/cindi/user-questionnaires/1/${idPessoa}`, {})
      .then((res: any) => {
        this.userQuestionnaire = res.data.data;
      });
  }
}
